import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Leaf, Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — sign in with your new password");
      navigate("/login");
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FBFBF9]" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-11 h-11 rounded-xl bg-[#14532D] flex items-center justify-center">
            <Leaf className="w-6 h-6 text-emerald-300" />
          </div>
          <div>
            <p className="font-extrabold text-lg tracking-tight text-stone-900" style={{ fontFamily: "Manrope" }}>Fern Hotels</p>
            <p className="text-[11px] text-stone-500 uppercase tracking-[0.15em]">Audit Management Platform</p>
          </div>
        </div>

        {!token ? (
          <div className="space-y-5" data-testid="reset-invalid-link">
            <h1 className="text-2xl font-bold tracking-tight text-stone-900">Invalid link</h1>
            <p className="text-sm text-stone-500">This reset link is missing its token. Please request a new one.</p>
            <Button variant="outline" className="w-full" data-testid="reset-request-new-link" onClick={() => navigate("/forgot-password")}>Request a new link</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="flex items-center gap-2 text-[#14532D]">
              <KeyRound className="w-6 h-6" strokeWidth={1.5} />
              <h1 className="text-2xl font-bold tracking-tight text-stone-900">Set a new password</h1>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rp-pass">New password</Label>
              <div className="relative">
                <Input id="rp-pass" data-testid="reset-password-input" type={show ? "text" : "password"} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters" className="bg-white pr-10" />
                <button type="button" data-testid="reset-toggle-visibility" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700">
                  {show ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rp-confirm">Confirm password</Label>
              <Input id="rp-confirm" data-testid="reset-confirm-input" type={show ? "text" : "password"} required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" className="bg-white" />
            </div>
            {error && <p data-testid="reset-error" className="text-sm text-rose-600">{error}</p>}
            <Button data-testid="reset-submit-button" type="submit" disabled={loading} className="w-full bg-[#14532D] hover:bg-[#166534]">
              {loading ? "Updating..." : "Update password"}
            </Button>
            <button type="button" data-testid="reset-back-to-login" onClick={() => navigate("/login")} className="text-sm text-stone-500 hover:text-stone-800 w-full text-center">Back to sign in</button>
          </form>
        )}
      </div>
    </div>
  );
}
